import { z } from 'zod';
import { entityBase, id, organizationId } from './common.js';

export const messageTrigger = z.enum([
  'hired',
  'shift_day_before',
  'shift_day_after',
  'shift_cancelled_by_office',
  'shift_cancelled_by_pro',
  'paid',
  'credential_approved',
  'credential_expiring',
]);
export type MessageTrigger = z.infer<typeof messageTrigger>;

export const messageTemplateChannel = z.enum(['in_app', 'email', 'sms']);
export type MessageTemplateChannel = z.infer<typeof messageTemplateChannel>;

export const messageTemplate = entityBase.extend({
  locationId: id.nullable(),
  trigger: messageTrigger,
  channel: messageTemplateChannel,
  subject: z.string().nullable(),
  body: z.string().min(1),
  enabled: z.boolean(),
});
export type MessageTemplate = z.infer<typeof messageTemplate>;

export const createMessageTemplateInput = z.object({
  clientRequestId: z.string().uuid().optional(),
  organizationId,
  locationId: id.nullish(),
  trigger: messageTrigger,
  channel: messageTemplateChannel.default('in_app'),
  subject: z.string().nullish(),
  body: z.string().min(1),
  enabled: z.boolean().default(true),
});
export type CreateMessageTemplateInput = z.infer<typeof createMessageTemplateInput>;

export const updateMessageTemplateInput = z.object({
  expectedUpdatedAt: z.string().min(1).max(100).optional(),
  locationId: id.nullish(),
  subject: z.string().nullish(),
  body: z.string().min(1).optional(),
  enabled: z.boolean().optional(),
  channel: messageTemplateChannel.optional(),
});
export type UpdateMessageTemplateInput = z.infer<typeof updateMessageTemplateInput>;

export const messageDeliveryStatus = z.enum(['pending', 'sent', 'failed']);
export type MessageDeliveryStatus = z.infer<typeof messageDeliveryStatus>;

export const messageDelivery = entityBase.extend({
  templateId: id.nullable(),
  trigger: messageTrigger,
  recipientUserId: id,
  shiftId: id.nullable(),
  bookingId: id.nullable(),
  notificationId: id.nullable(),
  externalNotificationId: id.nullable(),
  title: z.string().min(1),
  body: z.string(),
  status: messageDeliveryStatus,
});
export type MessageDelivery = z.infer<typeof messageDelivery>;

export const managedMessageTemplate = messageTemplate.extend({ canEdit: z.boolean() });
export const templateManagementAccess = z.object({
  canManageOrganizationDefaults: z.boolean(),
  locations: z.array(z.object({ id, name: z.string() })),
});
